import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, FileBarChart, Download, Printer } from 'lucide-react';
import { Card, CardBody, CardHeader } from '@/components/ui/Card';
import { escapeHtml } from '@/lib/escapeHtml';

function printTable(title: string, headers: string[], rows: (string | number)[][], footer?: (string | number)[]) {
  const w = window.open('', '_blank', 'width=1000,height=700');
  if (!w) return;
  const th = headers.map((h) => `<th>${escapeHtml(h)}</th>`).join('');
  const td = (c: string | number, i: number) => `<td class="${i > 0 ? 'num' : ''}">${escapeHtml(String(c ?? ''))}</td>`;
  const tbody = rows.map((r) => `<tr>${r.map(td).join('')}</tr>`).join('');
  const tfoot = footer ? `<tfoot><tr>${footer.map(td).join('')}</tr></tfoot>` : '';
  w.document.write(
    `<!doctype html><html><head><meta charset="utf-8"><title>${escapeHtml(title)}</title><style>` +
      `body{font-family:system-ui,Arial,sans-serif;padding:24px;color:#111}` +
      `h1{font-size:18px;margin:0 0 4px}.sub{color:#666;font-size:12px;margin-bottom:12px}` +
      `table{border-collapse:collapse;width:100%;font-size:12px}` +
      `th,td{border:1px solid #bbb;padding:4px 8px;text-align:left;vertical-align:top}` +
      `th{background:#eef6ee}td.num{text-align:right;font-variant-numeric:tabular-nums}` +
      `tfoot td{font-weight:bold;border-top:2px solid #888}@media print{body{padding:0}}` +
      `</style></head><body><h1>${escapeHtml(title)}</h1>` +
      `<div class="sub">Pnerp · ${new Date().toLocaleDateString('ro-RO')}</div>` +
      `<table><thead><tr>${th}</tr></thead><tbody>${tbody}</tbody>${tfoot}</table>` +
      `<script>window.onload=function(){window.focus();window.print();}<\/script></body></html>`,
  );
  w.document.close();
}

function downloadCsv(filename: string, rows: (string | number)[][]) {
  const esc = (v: string | number) => {
    const s = String(v ?? '');
    return /[",\n;]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const csv = rows.map((r) => r.map(esc).join(';')).join('\n');
  const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}
import { usePlanFaptReport, useAnexa78Report, useAnexa1Report } from '@/features/works/useWorks';
import { env } from '@/lib/env';

function downloadUrl(path: string, params: Record<string, string>) {
  const a = document.createElement('a');
  a.href = `${env.VITE_API_URL}/api/${path}?${new URLSearchParams(params).toString()}`;
  a.rel = 'noopener';
  a.click();
}

const lei = new Intl.NumberFormat('ro-RO', { maximumFractionDigits: 2 });
const CURRENT_YEAR = new Date().getFullYear();

export default function WorksReportsPage() {
  const { t } = useTranslation(['works', 'common']);
  const [tab, setTab] = useState<'planFapt' | 'anexa78' | 'anexa1'>('anexa78');
  const [year, setYear] = useState<number>(CURRENT_YEAR);

  const anexa = useAnexa78Report({ year }, tab === 'anexa78');
  const anexa1 = useAnexa1Report({ year }, tab === 'anexa1');
  const planFapt = usePlanFaptReport({ year }, tab === 'planFapt');

  const yearOptions: number[] = [];
  for (let y = CURRENT_YEAR + 1; y >= CURRENT_YEAR - 6; y--) yearOptions.push(y);

  const exportXlsx = () => {
    const p = { year: String(year) };
    const path =
      tab === 'anexa78'
        ? 'works/reports/anexa-78/export'
        : tab === 'anexa1'
          ? 'works/reports/anexa-1/export'
          : 'works/reports/plan-fapt/export';
    downloadUrl(path, p);
  };

  const printReport = () => {
    if (tab === 'anexa78') {
      printTable(
        `${t('works:reports.anexa78')} · ${year}`,
        [t('works:reports.workType'), t('works:reports.planTotal'), t('works:reports.faptTotal'), t('works:reports.fulfilment')],
        (anexa.data?.rows ?? []).map((r) => [r.workType, lei.format(r.planTotal), lei.format(r.faptTotal), `${r.fulfilmentPct}%`]),
        [t('works:reports.grandTotal'), lei.format(anexa.data?.grandPlan ?? 0), lei.format(anexa.data?.grandFapt ?? 0), ''],
      );
    } else if (tab === 'anexa1') {
      printTable(
        `${t('works:reports.anexa1')} · ${year}`,
        [t('works:reports.ocol'), t('works:reports.planArea'), t('works:reports.faptArea'), t('works:reports.planCost'), t('works:reports.faptCost')],
        (anexa1.data?.rows ?? []).map((r) => [r.ocolSilvic, lei.format(r.planArea), lei.format(r.faptArea), lei.format(r.planCost), lei.format(r.faptCost)]),
        [t('works:reports.grandTotal'), lei.format(anexa1.data?.totals.planArea ?? 0), lei.format(anexa1.data?.totals.faptArea ?? 0), lei.format(anexa1.data?.totals.planCost ?? 0), lei.format(anexa1.data?.totals.faptCost ?? 0)],
      );
    } else {
      printTable(
        `${t('works:reports.planFapt')} · ${year}`,
        ['Tr', t('works:table.primarie'), t('works:table.planFapt'), t('works:table.workType'), 'Salarizare', 'Contribuții', 'Combustibil', 'Servicii', t('works:table.total')],
        (planFapt.data?.rows ?? []).slice(0, 2000).map((r) => [
          r.quarter ?? '', r.primarieComposite ?? '', r.planFapt, r.workType ?? '',
          lei.format(r.wages), lei.format(r.contributions), lei.format(r.fuel), lei.format(r.services), lei.format(r.totalFinal),
        ]),
        ['', '', '', t('works:reports.grandTotal'),
          lei.format(planFapt.data?.totals.wages ?? 0), lei.format(planFapt.data?.totals.contributions ?? 0),
          lei.format(planFapt.data?.totals.fuel ?? 0), lei.format(planFapt.data?.totals.services ?? 0),
          lei.format(planFapt.data?.totals.totalFinal ?? 0)],
      );
    }
  };

  const exportCsv = () => {
    if (tab === 'anexa78') {
      const rows: (string | number)[][] = [
        [t('works:reports.workType'), t('works:reports.planTotal'), t('works:reports.faptTotal'), t('works:reports.fulfilment')],
        ...(anexa.data?.rows ?? []).map((r) => [r.workType, r.planTotal, r.faptTotal, r.fulfilmentPct]),
      ];
      downloadCsv(`anexa78_${year}.csv`, rows);
    } else if (tab === 'anexa1') {
      const rows: (string | number)[][] = [
        [t('works:reports.ocol'), t('works:reports.planArea'), t('works:reports.faptArea'), t('works:reports.planCost'), t('works:reports.faptCost')],
        ...(anexa1.data?.rows ?? []).map((r) => [r.ocolSilvic, r.planArea, r.faptArea, r.planCost, r.faptCost]),
      ];
      downloadCsv(`anexa1_${year}.csv`, rows);
    } else {
      const rows: (string | number)[][] = [
        ['Tr', t('works:table.primarie'), t('works:table.planFapt'), t('works:table.workType'), 'Salarizare', 'Contributii', 'Combustibil', 'Servicii', t('works:table.total')],
        ...(planFapt.data?.rows ?? []).map((r) => [
          r.quarter ?? '', r.primarieComposite ?? '', r.planFapt, r.workType ?? '', r.wages, r.contributions, r.fuel, r.services, r.totalFinal,
        ]),
      ];
      downloadCsv(`plan_fapt_${year}.csv`, rows);
    }
  };

  return (
    <div className="space-y-4 animate-fade-in">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="flex items-center gap-2 text-2xl font-bold text-slate-900 dark:text-slate-50">
            <FileBarChart className="h-6 w-6 text-forest-600" /> {t('works:reports.title')}
          </h1>
        </div>
        <Link to="/app/works" className="inline-flex items-center gap-1.5 text-sm text-forest-700 hover:underline">
          <ArrowLeft className="h-4 w-4" /> {t('works:actions.back')}
        </Link>
      </div>

      <Card>
        <CardBody className="flex flex-wrap items-center gap-3">
          <div className="flex flex-wrap gap-1 rounded bg-slate-100 p-1 dark:bg-slate-800">
            <TabBtn active={tab === 'anexa78'} onClick={() => setTab('anexa78')}>{t('works:reports.anexa78')}</TabBtn>
            <TabBtn active={tab === 'anexa1'} onClick={() => setTab('anexa1')}>{t('works:reports.anexa1')}</TabBtn>
            <TabBtn active={tab === 'planFapt'} onClick={() => setTab('planFapt')}>{t('works:reports.planFapt')}</TabBtn>
          </div>
          <label className="flex items-center gap-1.5 text-sm">
            {t('works:filters.year')}:
            <select
              value={year}
              onChange={(e) => setYear(Number(e.target.value))}
              className="rounded border border-slate-300 px-2 py-1 text-sm dark:border-slate-600 dark:bg-slate-800"
            >
              {yearOptions.map((y) => <option key={y} value={y}>{y}</option>)}
            </select>
          </label>
          <div className="ml-auto flex gap-2">
            <button onClick={printReport} className="inline-flex items-center gap-1.5 rounded border border-slate-300 bg-white px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-50 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-200">
              <Printer className="h-4 w-4" /> {t('works:reports.print', { defaultValue: 'Printează' })}
            </button>
            <button onClick={exportXlsx} className="inline-flex items-center gap-1.5 rounded bg-forest-600 px-3 py-1.5 text-sm font-semibold text-white hover:bg-forest-700">
              <Download className="h-4 w-4" /> XLSX
            </button>
            <button onClick={exportCsv} className="inline-flex items-center gap-1.5 rounded border border-slate-300 bg-white px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-50 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-200">
              <Download className="h-4 w-4" /> CSV
            </button>
          </div>
        </CardBody>
      </Card>

      {tab === 'anexa78' && (
        <Card>
          <CardHeader className="text-sm font-semibold">{t('works:reports.anexa78')} · {year}</CardHeader>
          <CardBody className="overflow-auto p-0">
            {anexa.isLoading ? (
              <div className="p-8 text-center text-sm text-slate-500">{t('works:loading')}</div>
            ) : (
              <table className="w-full border-collapse text-sm">
                <thead className="bg-slate-50 text-left text-xs uppercase text-slate-500 dark:bg-slate-800">
                  <tr>
                    <th className="px-3 py-2">{t('works:reports.workType')}</th>
                    <th className="px-3 py-2 text-right">{t('works:reports.planTotal')}</th>
                    <th className="px-3 py-2 text-right">{t('works:reports.faptTotal')}</th>
                    <th className="px-3 py-2 text-right">{t('works:reports.fulfilment')}</th>
                  </tr>
                </thead>
                <tbody>
                  {(anexa.data?.rows ?? []).map((r) => (
                    <tr key={r.workType} className="border-t border-slate-100 dark:border-slate-700">
                      <td className="max-w-[420px] truncate px-3 py-2" title={r.workType}>{r.workType}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{lei.format(r.planTotal)}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{lei.format(r.faptTotal)}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{r.fulfilmentPct}%</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="border-t-2 border-slate-300 font-semibold">
                    <td className="px-3 py-2">{t('works:reports.grandTotal')}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{lei.format(anexa.data?.grandPlan ?? 0)}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{lei.format(anexa.data?.grandFapt ?? 0)}</td>
                    <td className="px-3 py-2" />
                  </tr>
                </tfoot>
              </table>
            )}
          </CardBody>
        </Card>
      )}

      {tab === 'anexa1' && (
        <Card>
          <CardHeader className="text-sm font-semibold">{t('works:reports.anexa1')} · {year}</CardHeader>
          <CardBody className="overflow-auto p-0">
            {anexa1.isLoading ? (
              <div className="p-8 text-center text-sm text-slate-500">{t('works:loading')}</div>
            ) : (
              <table className="w-full border-collapse text-sm">
                <thead className="bg-slate-50 text-left text-xs uppercase text-slate-500 dark:bg-slate-800">
                  <tr>
                    <th className="px-3 py-2">{t('works:reports.ocol')}</th>
                    <th className="px-3 py-2 text-right">{t('works:reports.planArea')}</th>
                    <th className="px-3 py-2 text-right">{t('works:reports.faptArea')}</th>
                    <th className="px-3 py-2 text-right">{t('works:reports.planCost')}</th>
                    <th className="px-3 py-2 text-right">{t('works:reports.faptCost')}</th>
                  </tr>
                </thead>
                <tbody>
                  {(anexa1.data?.rows ?? []).map((r) => (
                    <tr key={r.ocolSilvic} className="border-t border-slate-100 dark:border-slate-700">
                      <td className="px-3 py-2">{r.ocolSilvic}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{lei.format(r.planArea)}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{lei.format(r.faptArea)}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{lei.format(r.planCost)}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{lei.format(r.faptCost)}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="border-t-2 border-slate-300 font-semibold">
                    <td className="px-3 py-2">{t('works:reports.grandTotal')}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{lei.format(anexa1.data?.totals.planArea ?? 0)}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{lei.format(anexa1.data?.totals.faptArea ?? 0)}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{lei.format(anexa1.data?.totals.planCost ?? 0)}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{lei.format(anexa1.data?.totals.faptCost ?? 0)}</td>
                  </tr>
                </tfoot>
              </table>
            )}
          </CardBody>
        </Card>
      )}

      {tab === 'planFapt' && (
        <Card>
          <CardHeader className="text-sm font-semibold">{t('works:reports.planFapt')} · {year}</CardHeader>
          <CardBody className="overflow-auto p-0">
            {planFapt.isLoading ? (
              <div className="p-8 text-center text-sm text-slate-500">{t('works:loading')}</div>
            ) : (
              <table className="w-full border-collapse text-xs">
                <thead className="bg-slate-50 text-left uppercase text-slate-500 dark:bg-slate-800">
                  <tr>
                    <th className="px-2 py-2">T</th>
                    <th className="px-2 py-2">{t('works:table.primarie')}</th>
                    <th className="px-2 py-2">{t('works:table.planFapt')}</th>
                    <th className="px-2 py-2">{t('works:table.workType')}</th>
                    <th className="px-2 py-2 text-right">Salariz.</th>
                    <th className="px-2 py-2 text-right">Contrib.</th>
                    <th className="px-2 py-2 text-right">Combust.</th>
                    <th className="px-2 py-2 text-right">Servicii</th>
                    <th className="px-2 py-2 text-right">{t('works:table.total')}</th>
                  </tr>
                </thead>
                <tbody>
                  {(planFapt.data?.rows ?? []).slice(0, 1000).map((r, i) => (
                    <tr key={i} className="border-t border-slate-100 dark:border-slate-700">
                      <td className="px-2 py-1">{r.quarter ?? '—'}</td>
                      <td className="px-2 py-1">{r.primarieComposite ?? '—'}</td>
                      <td className="px-2 py-1">{t(`works:planFapt.${r.planFapt}`)}</td>
                      <td className="max-w-[240px] truncate px-2 py-1" title={r.workType ?? ''}>{r.workType ?? '—'}</td>
                      <td className="px-2 py-1 text-right tabular-nums">{lei.format(r.wages)}</td>
                      <td className="px-2 py-1 text-right tabular-nums">{lei.format(r.contributions)}</td>
                      <td className="px-2 py-1 text-right tabular-nums">{lei.format(r.fuel)}</td>
                      <td className="px-2 py-1 text-right tabular-nums">{lei.format(r.services)}</td>
                      <td className="px-2 py-1 text-right font-medium tabular-nums">{lei.format(r.totalFinal)}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="border-t-2 border-slate-300 font-semibold">
                    <td className="px-2 py-2" colSpan={4}>{t('works:reports.grandTotal')}</td>
                    <td className="px-2 py-2 text-right tabular-nums">{lei.format(planFapt.data?.totals.wages ?? 0)}</td>
                    <td className="px-2 py-2 text-right tabular-nums">{lei.format(planFapt.data?.totals.contributions ?? 0)}</td>
                    <td className="px-2 py-2 text-right tabular-nums">{lei.format(planFapt.data?.totals.fuel ?? 0)}</td>
                    <td className="px-2 py-2 text-right tabular-nums">{lei.format(planFapt.data?.totals.services ?? 0)}</td>
                    <td className="px-2 py-2 text-right tabular-nums">{lei.format(planFapt.data?.totals.totalFinal ?? 0)}</td>
                  </tr>
                </tfoot>
              </table>
            )}
          </CardBody>
        </Card>
      )}
    </div>
  );
}

function TabBtn({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      onClick={onClick}
      className={`rounded px-3 py-1 text-sm font-medium ${active ? 'bg-white text-forest-700 shadow-sm dark:bg-slate-700' : 'text-slate-500'}`}
    >
      {children}
    </button>
  );
}
